import type {
  ModelResolutionInput,
  ModelResolutionNotFound,
} from "./sync/modelNameResolver/types.js";

export type AiToolsErrorCode =
  | "INVALID_INVOKE_INPUT"
  | "MODEL_PROFILE_INPUT_REJECTED"
  | "PROFILE_RESOLVE_KEY_REQUIRED"
  | "MODEL_NOT_FOUND"
  | "SYNC_FETCH_FAILED"
  | "SYNC_UPSERT_FAILED"
  | "COST_CALCULATION_FAILED"
  | "COST_UNKNOWN_MODEL"
  | "INVALID_USAGE_INPUT"
  | "OPENROUTER_AUTH_FAILED"
  | "OPENROUTER_MODELS_FETCH_FAILED"
  | "CACHE_READ_ERROR";

export class AiToolsError extends Error {
  constructor(
    public readonly code: AiToolsErrorCode,
    message: string,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = "AiToolsError";
  }
}

export class ModelNotFoundError extends AiToolsError {
  constructor(modelId: string) {
    super(
      "MODEL_NOT_FOUND",
      `Model not found: "${modelId}". Run "npx ai-tools catalog refresh" or verify the model id against the catalog.`,
    );
    this.name = "ModelNotFoundError";
  }
}

export class ModelProfileInputRejectedError extends AiToolsError {
  constructor(public readonly model: string) {
    super(
      "MODEL_PROFILE_INPUT_REJECTED",
      `Profile input "${model}" is not allowed when modelsOnly is true. Pass a concrete model id instead.`,
    );
    this.name = "ModelProfileInputRejectedError";
  }
}

export class ModelResolutionError extends AiToolsError {
  constructor(
    public readonly input: ModelResolutionInput,
    public readonly result: ModelResolutionNotFound,
  ) {
    super(
      "MODEL_NOT_FOUND",
      `Could not resolve model "${input.model}" (provider: "${input.provider ?? "unspecified"}"). ` +
        result.reason +
        (result.bestRejectedCandidate
          ? ` Best candidate was "${result.bestRejectedCandidate.modelId}" (confidence ${result.bestRejectedCandidate.confidence.toFixed(2)}).`
          : ""),
    );
    this.name = "ModelResolutionError";
  }
}

export class SyncError extends AiToolsError {
  constructor(code: Extract<AiToolsErrorCode, "SYNC_FETCH_FAILED" | "SYNC_UPSERT_FAILED" | "OPENROUTER_AUTH_FAILED" | "OPENROUTER_MODELS_FETCH_FAILED">, message: string, cause?: unknown) {
    super(code, message, cause);
    this.name = "SyncError";
  }
}

export class CostCalculationError extends AiToolsError {
  constructor(message: string, cause?: unknown) {
    super("COST_CALCULATION_FAILED", message, cause);
    this.name = "CostCalculationError";
  }
}

export class ProfileResolveKeyRequiredError extends AiToolsError {
  constructor(
    public readonly input: string,
    options?: { reason?: "bare_profile" | "not_profile_intent" },
  ) {
    const hint =
      options?.reason === "not_profile_intent"
        ? `Use a profile/choice key (e.g. "cheap/default") or a concrete model id.`
        : `Use "profile/choice" (e.g. "${input}/default") or pass choice explicitly.`;
    super(
      "PROFILE_RESOLVE_KEY_REQUIRED",
      `Profile resolve key required for "${input}". ${hint}`,
    );
    this.name = "ProfileResolveKeyRequiredError";
  }
}

export class UnknownModelCostError extends AiToolsError {
  constructor(
    public readonly model: string,
    public readonly provider: string,
  ) {
    super(
      "COST_UNKNOWN_MODEL",
      `Cannot price unknown model "${model}" (provider: "${provider}"). ` +
        'Refresh catalogs with "npx ai-tools catalog refresh", use a known profile/choice key, or set throwOnUnknownModel: false.',
    );
    this.name = "UnknownModelCostError";
  }
}
